/**
 * Menu do celular — o mesmo na home e no resto do site.
 *
 * Antes eram dois menus: o da home, herdado do template (um painel de vidro
 * translúcido com o dropdown de páginas em colunas, que no celular vazava pelas
 * bordas e deixava o botão da hero aparecer através), e o das páginas internas,
 * uma lista curta sem acesso a conta. Agora é uma tela cheia, de fundo sólido,
 * com três blocos na ordem em que a pessoa procura:
 *
 *   1. para onde ir — os links principais, grandes, fáceis de acertar com o dedo;
 *   2. o resto — regulamento, estatísticas e suporte, numa lista miúda;
 *   3. quem é você — "Entrar" ou "Criar conta" para visitante; para quem está
 *      logado, o time, o painel (organização) e sair.
 *
 * O acesso segue o padrão de entrar/criar conta: quem já tem conta entra,
 * quem não tem cria — e criar a conta aqui é o cadastro do time, que faz as
 * duas coisas de uma vez.
 */

import { useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { BotaoTema } from './BotaoTema';
import { LogoMPC, NomeDaMarca } from './LogoMPC';
import { TeamCrest } from './ui';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

const PRINCIPAIS = [
  { to: '/', rotulo: 'Início', end: true },
  { to: '/campeonatos', rotulo: 'Campeonatos', end: false },
  { to: '/rankings', rotulo: 'Rankings', end: false },
  { to: '/times', rotulo: 'Times', end: false },
  { to: '/loja', rotulo: 'Loja', end: false },
] as const;

const SECUNDARIOS = [
  { to: '/regulamento', rotulo: 'Regulamento' },
  { to: '/estatisticas', rotulo: 'Estatísticas' },
  { to: '/suporte', rotulo: 'Suporte' },
] as const;

export function MenuCelular({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const { user, teams, isStaff, logout } = useAuth();
  const local = useLocation();
  const time = teams[0];

  // Trocar de rota fecha o menu.
  useEffect(() => {
    onFechar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.pathname]);

  // Com o menu aberto, a página de trás não rola junto com o dedo.
  useEffect(() => {
    if (!aberto) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onFechar();
    window.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener('keydown', esc);
    };
  }, [aberto, onFechar]);

  return (
    <div
      id="menu-celular"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      aria-hidden={!aberto}
      className={cn(
        'fixed inset-0 z-[70] flex flex-col bg-ink text-paper transition-[opacity,visibility] duration-300 lg:hidden',
        aberto ? 'visible opacity-100' : 'invisible opacity-0',
      )}
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      {/* Luz da marca no topo: o menu é uma tela da MPC, não uma lista solta. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(ellipse_at_50%_-20%,rgb(197_210_0/0.18),transparent_70%)]"
      />

      {/* -------------------------------------------------- barra */}
      <div className="relative flex items-center justify-between px-5 pt-5 pb-3">
        <Link to="/" className="flex items-center" aria-label="MPC — página inicial">
          <LogoMPC altura={44} />
          <NomeDaMarca />
        </Link>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar menu"
          className="grid size-11 place-items-center rounded-full bg-(--vidro) transition-colors hover:text-brand-ink"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="size-5" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div
        className={cn(
          'relative flex flex-1 flex-col overflow-y-auto px-5 pb-6 transition-transform duration-300 ease-[--ease-signature]',
          aberto ? 'translate-y-0' : 'translate-y-3',
        )}
      >
        {/* -------------------------------------------------- principais */}
        <nav aria-label="Principal" className="mt-4">
          {[...PRINCIPAIS, ...(isStaff ? [{ to: '/admin', rotulo: 'Painel', end: true }] : [])].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'group flex items-center justify-between border-b border-line/60 py-3.5',
                  isActive ? 'text-brand-ink' : 'text-paper',
                )
              }
            >
              <span className="mpc-display text-[2rem] leading-none tracking-wide uppercase">{item.rotulo}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 opacity-40 transition-transform group-hover:translate-x-1 group-hover:opacity-100" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </NavLink>
          ))}
        </nav>

        {/* -------------------------------------------------- o resto */}
        <nav aria-label="Mais páginas" className="mt-6 flex flex-wrap gap-2">
          {SECUNDARIOS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-full border border-line px-4 py-2 text-sm text-muted transition-colors hover:border-brand/50 hover:text-paper"
            >
              {item.rotulo}
            </Link>
          ))}
        </nav>

        <div className="mt-6 flex items-center justify-between rounded-xl border border-line px-4 py-2.5">
          <span className="text-sm text-muted">Tema</span>
          <BotaoTema />
        </div>

        {/* -------------------------------------------------- acesso */}
        <div className="mt-auto pt-8">
          {user ? (
            <div className="rounded-2xl border border-line bg-surface p-4">
              <div className="flex items-center gap-3">
                {time ? (
                  <TeamCrest team={{ name: time.name, shortName: time.shortName, crestUrl: time.crestUrl ?? null }} size="md" />
                ) : (
                  <span className="grid size-9 place-items-center rounded-full bg-brand text-sm font-semibold text-black">
                    {user.displayName.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{user.displayName}</p>
                  <p className="truncate text-xs text-faint">{user.email}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {time ? (
                  <Link
                    to={`/times/${time.slug}`}
                    className="rounded-full bg-brand px-4 py-2.5 text-center text-sm font-medium text-black transition-colors hover:bg-brand-bright"
                  >
                    Meu time
                  </Link>
                ) : (
                  <Link
                    to="/meu-time/novo"
                    className="rounded-full bg-brand px-4 py-2.5 text-center text-sm font-medium text-black transition-colors hover:bg-brand-bright"
                  >
                    Cadastrar time
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onFechar();
                    logout();
                  }}
                  className="rounded-full border border-line-strong px-4 py-2.5 text-sm font-medium transition-colors hover:border-loss/60 hover:text-loss"
                >
                  Sair
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-2.5">
                <Link
                  to="/entrar"
                  className="rounded-full border border-line-strong px-4 py-3 text-center text-base font-medium transition-colors hover:border-brand/60"
                >
                  Entrar
                </Link>
                <Link
                  to="/cadastrar-time"
                  className="rounded-full bg-brand px-4 py-3 text-center text-base font-medium text-black transition-colors hover:bg-brand-bright"
                >
                  Criar conta
                </Link>
              </div>
              <p className="mt-3 text-center text-xs leading-relaxed text-faint">
                Já tem conta? Entre. Primeira vez? Crie a conta e cadastre o time de uma vez.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
